/**
 * Represents an error when non-existent context value referenced. I.e. when requested context entry has no value.
 */
class CxReferenceError extends ReferenceError {
    /**
     * Constructs context context reference error.
     *
     * @param entry - Illegally referred context entry.
     * @param message - Arbitrary error message.
     * @param reason - Original error reason.
     */
    constructor(entry, message = `The ${entry} has no value`, reason) {
        super(reason === undefined ? message : `${message}. ${reason}`);
        this.entry = entry;
        this.reason = reason;
    }
    get name() {
        return 'CxReferenceError';
    }
}

/**
 * Creates unavailable context entry accessor function.
 *
 * The created function would throw a {@link CxReferenceError} on each call.
 *
 * @param entry - The unavailable entry.
 * @param message - Error message.
 * @param reason - The reason why entry is no longer available.
 *
 * @returns A no-arg function that always throws.
 */
function cxUnavailable(entry, message, reason) {
    return () => {
        throw new CxReferenceError(entry, message || `The ${entry} is unavailable`, reason);
    };
}

function CxEntry$assignOnce(target, getValue) {
    const get = target.lazy(getValue);
    return receiver => {
        const value = get();
        if (value != null) {
            receiver(value);
        }
    };
}

/**
 * Creates array-valued context entry definer.
 *
 * The entry value is a read-only array of assets.
 *
 * The entry value is evaluated at most once per context.
 *
 * @typeParam TElement - Array element type. The same as entry value asset type.
 * @param byDefault - Creates {@link CxEntry.Definition.assignDefault default} entry value. Accepts entry definition
 * target as the only parameter. Empty array will be used as default value when omitted.
 *
 * @returns New context entry definer.
 */
function cxArray({ byDefault = cxArray$byDefault, } = {}) {
    return target => ({
        assign: CxEntry$assignOnce(target, cxArray$value),
        assignDefault: CxEntry$assignOnce(target, byDefault),
    });
}
function cxArray$byDefault(_target) {
    return [];
}
function cxArray$value(target) {
    const array = [];
    target.eachAsset(asset => {
        array.push(asset);
    });
    return array.length ? array : null;
}

function CxTracker$assign({ to }, _target) {
    return to;
}
function CxTracker$create(target, trackAssets, getDefault) {
    const trackerSupply = target.supply.derive();
    const track = getDefault
        ? (receiver, { supply } = {}) => trackAssets((value, by) => {
            if (by != null) {
                receiver(value, by);
            }
            else {
                receiver(getDefault(), -1 /* Defaults */);
            }
        }, trackerSupply.derive(supply))
        : (receiver, { supply } = {}) => trackAssets(receiver, trackerSupply.derive(supply));
    let get;
    let to;
    const init = () => track((value, by) => {
        if (by != null) {
            get = () => value;
            to = receiver => receiver(value, by);
        }
        else {
            get = cxUnavailable(target.entry);
            to = CxTracker$empty$to;
        }
    }, trackerSupply);
    get = () => {
        init();
        return get();
    };
    to = (receiver) => {
        init();
        to(receiver);
    };
    trackerSupply.whenOff(reason => {
        get = to = cxUnavailable(target.entry, `The ${target.entry} is no longer available`, reason);
    });
    return {
        supply: trackerSupply,
        get: () => get(),
        to: receiver => to(receiver),
        track,
    };
}
function CxTracker$default(target, getDefault) {
    return CxTracker$create(target, (receiver, { supply } = {}) => {
        supply = target.supply.derive(supply);
        if (!supply.isOff) {
            receiver();
        }
        return supply;
    }, getDefault);
}
function CxTracker$empty$to(_receiver) {
    // No current value
}

function cxDynamic({ create, byDefault, assign = CxTracker$assign, } = {}) {
    return target => {
        if (!byDefault && !create) {
            byDefault = cxDynamic$byDefault;
        }
        create || (create = cxDynamic$create);
        const getDefault = byDefault && target.lazy(byDefault);
        const tracker = CxTracker$create(target, receiver => target.trackAssetList(list => {
            const assets = [];
            for (const provided of list) {
                provided.eachAsset(asset => {
                    assets.push(asset);
                });
            }
            return assets.length
                ? receiver(create(assets, target), 1 /* Assets */)
                : receiver();
        }), getDefault);
        const defaultTracker = CxTracker$default(target, getDefault);
        return {
            assign: assign(tracker, target),
            assignDefault: assign(defaultTracker, target),
        };
    };
}
function cxDynamic$create(assets, _target) {
    return assets;
}
function cxDynamic$byDefault() {
    return [];
}

/**
 * Creates evaluating context entry definer.
 *
 * Evaluates entry value at most once per context.
 *
 * @typeParam TValue - Combined context value type.
 * @typeParam TAsset - A type of context value assets to combine.
 * @param evaluate - Evaluates entry value out. Accepts entry definition target as the only parameter.
 * @param byDefault - Evaluates {@link CxEntry.Definition.assignDefault default} entry value. Accepts entry definition
 * target as the only parameter. Evaluates to nothing by default.
 *
 * @returns New context entry definer.
 */
function cxEvaluated(evaluate, { byDefault = () => null, } = {}) {
    return target => ({
        assign: CxEntry$assignOnce(target, evaluate),
        assignDefault: CxEntry$assignOnce(target, byDefault),
    });
}

/**
 * Creates single-value context entry definer.
 *
 * Treats the last provided asset as entry value and ignores the rest of them.
 *
 * The entry value is evaluated at most once per context.
 *
 * @typeParam TValue - Context value type.
 * @param byDefault - Creates {@link CxEntry.Definition.assignDefault default} entry value. Accepts entry definition
 * target as the only parameter.
 *
 * @returns New context entry definer.
 */
function cxSingle({ byDefault, } = {}) {
    return target => ({
        assign: CxEntry$assignOnce(target, cxSingle$value),
        assignDefault: byDefault && CxEntry$assignOnce(target, byDefault),
    });
}
function cxSingle$value(target) {
    return target.recentAsset;
}

/**
 * Context entry containing {@link CxGlobals global values context}.
 *
 * Services may utilize this instance as a {@link cxScoped scope} to make sure the service instance is singleton.
 *
 * It is an application (or framework) responsibility to provide a global context.
 */
const CxGlobals = {
    perContext: ( /*#__PURE__*/cxSingle()),
    toString: () => '[CxGlobal]',
};

function cxRecent({ create = cxRecent$create, byDefault, assign = CxTracker$assign, } = {}) {
    return target => {
        const getDefault = byDefault && target.lazy(byDefault);
        const tracker = CxTracker$create(target, receiver => target.trackRecentAsset(evaluated => evaluated
            ? receiver(create(evaluated.asset, target), 1 /* Assets */)
            : receiver()), getDefault);
        const defaultTracker = CxTracker$default(target, getDefault);
        return {
            assign: assign(tracker, target),
            assignDefault: assign(defaultTracker, target),
        };
    };
}
function cxRecent$create(recent, _target) {
    return recent;
}

/**
 * Creates scoped context value definer.
 *
 * Here "scope" is a context available as an entry of itself. Thus context derived from that scope may access it via
 * the same entry.
 *
 * Scoped context value is the one available only in particular scope only.
 *
 * The entry definer created by this function accesses the scope, and then requests the value from that scope. Thus the
 * value would be the same regardless the context it is requested from.
 *
 * @param scope - Context entry containing target scope as its value.
 * @param definer - An entry definer that defines the entry in target `scope`.
 *
 * @returns New scoped context entry definer.
 */
function cxScoped(scope, definer) {
    return target => {
        const context = target.get(scope);
        if (context === target.context) {
            return definer(target);
        }
        return {
            assign(set, request) {
                if (request.by /* explicit request method */) {
                    // Wrap request to handle fallback value.
                    context.get(target.entry, cxScoped$request(1 /* Assets */, set, request));
                }
                else {
                    // Perform full request.
                    // The `assignDefault()` won't assign any value in this case.
                    context.get(target.entry, { ...request, set });
                }
            },
            assignDefault(set, request) {
                if (request.by /* explicit (defaults) request method */) {
                    // Wrap request to handle fallback value.
                    context.get(target.entry, cxScoped$request(-1 /* Defaults */, set, request));
                }
            },
        };
    };
}
/**
 * Creates value definer with scoped default value.
 *
 * Unlike {@link cxScoped}, the definer created by this function scopes only {@link CxEntry.Definition.assignDefault
 * default value}, while the {@link CxEntry.Definition.assign value provided by assets} is always evaluated in the
 * requested context.
 *
 * @param scope - Context entry containing target scope as its value.
 * @param definer - An entry definer that defines the entry.
 *
 * @returns New scoped context entry definer.
 */
function cxDefaultScoped(scope, definer) {
    return target => {
        const context = target.get(scope);
        if (context === target.context) {
            return definer(target);
        }
        const getDefiner = target.lazy(definer);
        return {
            assign(receiver, request) {
                var _a, _b;
                (_b = (_a = getDefiner()).assign) === null || _b === void 0 ? void 0 : _b.call(_a, receiver, request);
            },
            assignDefault(receiver, request) {
                // Wrap request to handle fallback value.
                context.get(target.entry, cxScoped$request(-1 /* Defaults */, receiver, request));
            },
        };
    };
}
function cxScoped$request(by, receiver, request) {
    let { or } = request;
    let set;
    if (or !== undefined /* fallback specified */) {
        // Pass the value through.
        set = receiver;
    }
    else {
        // Set fallback to `null` and ignore any fallback received.
        or = null;
        set = (value, receivedBy = by) => receivedBy && receiver(value, receivedBy);
    }
    return {
        ...request,
        or,
        by,
        set,
    };
}

export { CxGlobals, CxReferenceError, cxArray, cxDefaultScoped, cxDynamic, cxEvaluated, cxRecent, cxScoped, cxSingle, cxUnavailable };
//# sourceMappingURL=context-values.js.map
